/**
 * 全站补充结构化数据（JSON-LD）构建模块
 * 供 src/App.vue 在 syncStructuredData 中调用，按路由追加对应 schema：
 * - /products/tape               -> Product + FAQPage（FAQPage 仅英文）
 * - /news/<slug>（新闻正文）       -> NewsArticle
 * - /news/company                 -> ItemList（最新新闻列表）
 * - /recruit                      -> JobPosting（按语言过滤岗位）
 * - /contact、/contact/info       -> ContactPage
 * 所有数据均来自站点既有 i18n 内容，不引入未经核实的字段。
 */
import zhNews from '@/i18n/zh/pages/news'
import enNews from '@/i18n/en/pages/news'
import zhRecruit from '@/i18n/zh/pages/recruit'
import enRecruit from '@/i18n/en/pages/recruit'
import { faqItems, stripHtml } from './faq'

export type SeoLocale = 'zh-CN' | 'en-US'

export interface RouteSchemaOptions {
  locale: SeoLocale
  /** 站点主域名（不带尾部斜杠），用于拼绝对地址 */
  siteOrigin: string
  companyName: string
  siteName: string
  /** 当前页绝对地址（含语言前缀） */
  currentPageUrl: string
  /** 当前页标题（用于 ContactPage 等 schema 的 name） */
  pageTitle: string
}

interface NewsMeta {
  id: string
  title: string
  url?: string
  date?: string
  image?: string
  content?: string
}

type RecruitJobs = Record<string, { title: string; details: string }>

const newsLists: Record<SeoLocale, () => NewsMeta[]> = {
  'zh-CN': () => (zhNews as any)?.company?.news?.list ?? [],
  'en-US': () => (enNews as any)?.company?.news?.list ?? []
}

const recruitData: Record<SeoLocale, RecruitJobs> = {
  'zh-CN': (zhRecruit as any)?.jobs ?? {},
  'en-US': (enRecruit as any)?.jobs ?? {}
}

/** 英文站不展示的岗位（与招聘页 lang 过滤逻辑保持一致） */
const zhOnlyJobKeys = new Set(['cableProjectManager', 'salesSpecialist'])

const toAbsolute = (siteOrigin: string, pathOrUrl: string) =>
  /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : `${siteOrigin}${pathOrUrl}`

const toExcerpt = (text: string | undefined, max = 160): string => {
  if (!text) return ''
  const first = text.split('---')[0] ?? ''
  const clean = stripHtml(first).replace(/\s+/g, ' ').trim()
  return clean.length > max ? `${clean.slice(0, max)}…` : clean
}

export const getNewsItem = (locale: SeoLocale, id: string): NewsMeta | undefined =>
  newsLists[locale]().find((item) => item.id === id)

export const getLatestNews = (locale: SeoLocale, limit = 10): NewsMeta[] =>
  newsLists[locale]().slice(0, limit)

const buildProductSchema = (opts: RouteSchemaOptions): Record<string, unknown> => {
  const { locale, currentPageUrl, siteOrigin, companyName } = opts
  const isZh = locale === 'zh-CN'
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${currentPageUrl}#product`,
    url: currentPageUrl,
    name: isZh
      ? '第二代高温超导带材（REBCO）'
      : '2G High-Temperature Superconducting Tape (REBCO)',
    description: isZh
      ? '上海超导量产的第二代高温超导带材（REBCO 镀膜导体），提供 3mm、4mm、12mm 等宽度，涵盖常规、高性能、超高性能等级，77K 自场临界电流最高可达 800A（12mm），可选镀铜、紫铜封装、不锈钢封装等后处理方案，单根长度 100m~1000m。'
      : 'Shanghai Superconductor mass-produces 2G high-temperature superconducting tapes (REBCO coated conductors) in 3mm, 4mm and 12mm widths with Standard, High-Performance and Ultra-Performance grades. Critical current reaches up to 800A (12mm) at 77K self-field. Copper plating, copper lamination and SS316 lamination options are available, with single-piece lengths of 100m~1000m.',
    image: toAbsolute(siteOrigin, '/images/products/带材图.jpg'),
    brand: {
      '@type': 'Brand',
      name: companyName
    },
    category: isZh ? '高温超导带材' : 'High-temperature superconducting tape',
    material: 'REBCO coated conductor',
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'Width', value: '3mm / 4mm / 12mm' },
      { '@type': 'PropertyValue', name: 'Ic (77K, self-field)', value: '85A - 800A (by width & grade)' },
      { '@type': 'PropertyValue', name: 'Ic (4.2K, 10T)', value: '260A - 2400A (by width & grade)' },
      { '@type': 'PropertyValue', name: 'Single piece length', value: '100m - 1000m' }
    ]
  }
}

const buildFaqPageSchema = (opts: RouteSchemaOptions): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${opts.currentPageUrl}#faq`,
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripHtml(item.a)
    }
  }))
})

const buildNewsArticleSchema = (
  item: NewsMeta,
  opts: RouteSchemaOptions
): Record<string, unknown> => {
  const { currentPageUrl, siteOrigin, locale } = opts
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    '@id': `${currentPageUrl}#article`,
    url: currentPageUrl,
    headline: item.title,
    inLanguage: locale,
    author: { '@id': `${siteOrigin}/#organization` },
    publisher: { '@id': `${siteOrigin}/#organization` },
    description: toExcerpt(item.content, 160),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentPageUrl
    }
  }
  if (item.date) {
    schema.datePublished = item.date
    schema.dateModified = item.date
  }
  if (item.image) {
    schema.image = toAbsolute(siteOrigin, item.image)
  }
  return schema
}

const buildJobPostingSchemas = (opts: RouteSchemaOptions): Record<string, unknown>[] => {
  const { locale, currentPageUrl, siteOrigin, companyName } = opts
  const jobs = recruitData[locale]
  return Object.entries(jobs)
    .filter(([key]) => !(locale === 'en-US' && zhOnlyJobKeys.has(key)))
    .map(([key, job]) => ({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      '@id': `${currentPageUrl}#job-${key}`,
      title: job.title,
      description: toExcerpt(job.details, 300),
      hiringOrganization: {
        '@type': 'Organization',
        name: companyName,
        sameAs: siteOrigin
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress:
            locale === 'zh-CN' ? '上海市浦东新区康威路781号' : '781 Kangwei Road, Pudong New District',
          addressLocality: locale === 'zh-CN' ? '上海市' : 'Shanghai',
          addressRegion: locale === 'zh-CN' ? '上海' : 'Shanghai',
          addressCountry: 'CN'
        }
      }
    }))
}

const buildContactPageSchema = (opts: RouteSchemaOptions): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${opts.currentPageUrl}#contactpage`,
  url: opts.currentPageUrl,
  name: opts.pageTitle,
  description: opts.pageTitle,
  inLanguage: opts.locale,
  mainEntity: {
    '@id': `${opts.siteOrigin}/#organization`
  }
})

const buildNewsItemListSchema = (
  items: NewsMeta[],
  opts: RouteSchemaOptions
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  '@id': `${opts.currentPageUrl}#newslist`,
  name: opts.pageTitle,
  itemListElement: items
    .filter((item) => item.id)
    .map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      url: toAbsolute(opts.siteOrigin, item.url || `/news/${item.id}`)
    }))
})

/**
 * 根据基础路径返回需要追加的 schema 列表（不包含 Organization/WebSite/WebPage/BreadcrumbList，
 * 这些由 App.vue 统一构建）。
 */
export const buildRouteSchemas = (
  basePathname: string,
  opts: RouteSchemaOptions
): Record<string, unknown>[] => {
  const { locale, currentPageUrl } = opts
  const schemas: Record<string, unknown>[] = []

  if (basePathname === '/products/tape') {
    schemas.push(buildProductSchema(opts))
    // FAQ 区块仅在英文站展示，FAQPage 结构数据只在英文站输出
    if (locale === 'en-US') {
      schemas.push(buildFaqPageSchema(opts))
    }
  }

  const newsMatch = basePathname.match(/^\/news\/([^/]+)$/)
  if (newsMatch && newsMatch[1] !== 'company' && newsMatch[1] !== 'downloads') {
    const item = getNewsItem(locale, newsMatch[1])
    if (item && item.title) {
      schemas.push(buildNewsArticleSchema(item, opts))
    }
  } else if (basePathname === '/news/company') {
    const latest = getLatestNews(locale, 10)
    if (latest.length > 0) {
      schemas.push(buildNewsItemListSchema(latest, opts))
    }
  }

  if (basePathname === '/recruit') {
    schemas.push(...buildJobPostingSchemas(opts))
  }

  if (basePathname === '/contact' || basePathname === '/contact/info') {
    schemas.push(buildContactPageSchema(opts))
  }

  return schemas
}
